// @ts-nocheck

import React, { useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';

import ExtrasIconSvgAsComponent from '../../assets/svg/tab-bar/ExtrasIconSvgAsComponent';
import HomeIconSvgAsComponent from '../../assets/svg/tab-bar/HomeIconSvgAsComponent';
import LiveIconSvgAsComponent from '../../assets/svg/tab-bar/LiveIconSvgAsComponent';
import MyBetsIconSvgAsComponent from '../../assets/svg/tab-bar/MyBetsIconSvgAsComponent';
import SportsIconSvgAsComponent from '../../assets/svg/tab-bar/SportsIconSvgAsComponent';
import { TABBAR_ITEM_TRANSITION } from '../../constants/ui/tabbar';
import BoomNavLabelContainer from '../../modules/boom/components/BoomNavLabel/BoomNavLabelContainer';
import BoomNavIconContainer from '../../modules/boom/components/common/BoomNavIcon/BoomNavIconContainer';
import GRIcon from '../../modules/gift-race/components/GRIcon';
import KLIcon from '../../modules/koh-lanta/components/KLIcon';
import MPIcon from '../../modules/monopoly/components/MPIcon';
import theme from '../../styled-components/dark';
import { BetslipFilters } from '../../types/history';
import { type TabBarNavigationType, TabBarNavigationTypes, TabBarAnchorIdsByNavigationTypes } from '../../types/tabBar';
import RoutesRedirectionFactory from '../../utils/routes/RoutesRedirectionFactory';
import { SMOOTH_SCROLL_TIMEOUT, smartScrollToRootAnchor } from '../../utils/smoothScroll';
import svgResolver from '../../utils/tabBar';

const RouteConfigByNavigationType = new Proxy(
  {
    [TabBarNavigationTypes.HOME]: RoutesRedirectionFactory.getHomeRouteConfig(),
    [TabBarNavigationTypes.SPORTS]: RoutesRedirectionFactory.getSportsMenuRouteConfig(),
    [TabBarNavigationTypes.LIVE]: RoutesRedirectionFactory.getLiveRouteConfig(),
    [TabBarNavigationTypes.BASKET]: RoutesRedirectionFactory.getBasketRouteConfig(),
    [TabBarNavigationTypes.MY_BETS]: RoutesRedirectionFactory.getHistoryRouteConfig(BetslipFilters.RUNNING),
    [TabBarNavigationTypes.GRIDS]: RoutesRedirectionFactory.getGridsRouteConfig(),
    [TabBarNavigationTypes.GRIDS_HISTORY]: RoutesRedirectionFactory.getGridsHistoryRouteConfig(),
    [TabBarNavigationTypes.KOH_LANTA]: RoutesRedirectionFactory.getKLRouteConfig(),
    [TabBarNavigationTypes.GIFT_RACE]: RoutesRedirectionFactory.getGRRouteConfig(),
    [TabBarNavigationTypes.MONOPOLY]: RoutesRedirectionFactory.getMPRouteConfig(),
    [TabBarNavigationTypes.BOOM]: RoutesRedirectionFactory.getBoomRouteConfig(),
    [TabBarNavigationTypes.EXTRAS]: RoutesRedirectionFactory.getExtrasRouteConfig(),
  },
  {
    get(origin, key) {
      if (!origin[key]) {
        return null;
      }
      return origin[key];
    },
  },
);

const getCounterColor = (isHighlight) => {
  if (isHighlight) {
    return theme.TabBar.Button.default.active.backgroundColor;
  }
  return theme.TabBar.Button.default.passive.backgroundColor;
};

const getCounterBackgroundColor = (isHighlight) => {
  if (isHighlight) {
    return theme.TabBar.Button.default.active.color;
  }
  return theme.TabBar.Button.default.passive.color;
};

const getTabBarItemBackgroundColor = getCounterColor;

const getLabelColor = getCounterBackgroundColor;

const TabBarItemWrapper = styled.div`
  position: relative;
  width: 100%;
  border-radius: 0.33em;
  height: calc(100% - 0.33em);

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
  text-align: center;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  cursor: pointer;

  background-color: ${({ $isHighlight }) => getTabBarItemBackgroundColor($isHighlight)};
`;

const Counter = styled.div`
  position: absolute;
  font-family: 'BettingPoliticaExtraBold';

  top: 5.5%;
  right: 5%;
  transform: translateX(50%);

  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;

  font-size: 50%;
  transition: none ${TABBAR_ITEM_TRANSITION}ms ease-in-out;
  transition-property: color, background-color;

  ${({ $isHighlight, $pad }) => {
    return css`
      color: ${getCounterColor($isHighlight)};
      background-color: ${getCounterBackgroundColor($isHighlight)};

      height: 2em;
      width: 2em;

      padding-left: ${$pad ? 0.2 : 0}em;
    `;
  }}
`;

const spacing = 0.15;

const IconWrapper = styled.div`
  position: relative;

  height: 2em;

  margin-top: ${spacing + 0.1}em;
  margin-bottom: -${spacing}em;

  & svg {
    height: 100%;
    width: auto;
  }
`;

const labelPadding = 0.1;

const sharedLabelStyle = css`
  width: calc(100% - ${labelPadding}em);
  font-size: 0.75em;

  display: block;
  overflow: hidden;

  margin-bottom: ${spacing}em;
  padding: ${labelPadding}em;

  text-align: center;
  white-space: nowrap;
  transition: color ${TABBAR_ITEM_TRANSITION}ms ease-in-out;
`;

const CommonLabel = styled.span`
  ${sharedLabelStyle};
  color: ${({ $isHighlight }) => getLabelColor($isHighlight)};
`;

const StyledBoomLabelContainer = styled(BoomNavLabelContainer)`
  ${sharedLabelStyle}
`;

const pulseAnim = keyframes`
  50% {
    opacity: 1;
  }
`;

const UnseenChangesTag = styled.div`
  position: absolute;
  right: 0;
  top: 10%;
  width: 0.3em;
  height: 0.3em;

  border-radius: 50%;
  background-color: ${theme.TabBar.Button.UnseenChangesTag.backgroundColor};
  opacity: 0.75;

  animation: ${pulseAnim} 2000ms ease-in-out infinite;
`;

const RawSvg = styled.svg`
  transition: fill ${TABBAR_ITEM_TRANSITION}ms ease-in-out;

  ${({ $isActive }) =>
    $isActive
      ? css`
          fill: ${theme.TabBar.Button.default.active.color};
        `
      : css`
          fill: ${theme.TabBar.Button.default.passive.color};
        `}
`;

export type NavigationTabBarItemProps = {
  id: TabBarNavigationType;
  label: string;
  counter?: number;
  isHighlight: boolean;
  onClick: (e: unknown) => void;
  disableAnimation?: boolean;
  className?: string;
};

const NavigationTabBarItem = ({
  id,
  label,
  onClick,
  counter,
  isHighlight,
  disableAnimation = false,
  className,
}: NavigationTabBarItemProps) => {
  const wrapperRef = useRef();
  const location = useLocation();

  const displayedCounter = useMemo(() => {
    if (counter) {
      if (counter > 99) {
        return '99+';
      }
      return `${counter}`;
    }
    return null;
  }, [counter]);

  const displayUnseenChangesTag = useMemo(
    () => id === TabBarNavigationTypes.GIFT_RACE && !disableAnimation,
    [id, disableAnimation],
  );

  const SmartIcon = useMemo(() => {
    if (id === TabBarNavigationTypes.KOH_LANTA) {
      return <KLIcon disableAnimation={disableAnimation} isHighlight={isHighlight} />;
    }

    if (id === TabBarNavigationTypes.GIFT_RACE) {
      return <GRIcon isHighlight={isHighlight} />;
    }

    if (id === TabBarNavigationTypes.HOME) {
      return <HomeIconSvgAsComponent isActive={isHighlight} />;
    }

    if (id === TabBarNavigationTypes.LIVE) {
      return <LiveIconSvgAsComponent isActive={isHighlight} hasCounter={!!displayedCounter} />;
    }

    if (id === TabBarNavigationTypes.MY_BETS || id === TabBarNavigationTypes.GRIDS_HISTORY) {
      return <MyBetsIconSvgAsComponent isActive={isHighlight} />;
    }

    if (id === TabBarNavigationTypes.SPORTS) {
      return <SportsIconSvgAsComponent isActive={isHighlight} />;
    }

    if (id === TabBarNavigationTypes.EXTRAS) {
      return <ExtrasIconSvgAsComponent isActive={isHighlight} disableAnimation={disableAnimation} />;
    }

    if (id === TabBarNavigationTypes.MONOPOLY) {
      return <MPIcon disableAnimation={disableAnimation} />;
    }

    if (id === TabBarNavigationTypes.BOOM) {
      return (
        <BoomNavIconContainer data-testid={'boom-navicon'} isActive={isHighlight} disableAnimation={disableAnimation} />
      );
    }

    const RawSvgResolved = svgResolver[id];

    return <RawSvg as={RawSvgResolved} $isActive={isHighlight} />;
  }, [isHighlight, id, disableAnimation, displayedCounter]);

  const onItemClick = useCallback(
    async (e) => {
      onClick(e);
      if (isHighlight) {
        const anchorId = TabBarAnchorIdsByNavigationTypes[id];
        if (RouteConfigByNavigationType[id]?.pathname === location.pathname) {
          await smartScrollToRootAnchor(anchorId, SMOOTH_SCROLL_TIMEOUT, 100);
        } else {
          await smartScrollToRootAnchor(anchorId, 0, 0);
        }
      }
    },
    [isHighlight, id, onClick, location],
  );

  const SmartLabel = useMemo(() => {
    if (id === TabBarNavigationTypes.BOOM) {
      return <StyledBoomLabelContainer isHighlight={isHighlight} />;
    }

    return <CommonLabel $isHighlight={isHighlight}>{label}</CommonLabel>;
  }, [id, isHighlight, label]);

  return (
    <TabBarItemWrapper
      id={`tabbar-item-${id}`}
      data-testid={`tabbar-item-${id}`}
      className={className}
      onClick={onItemClick}
      $isHighlight={isHighlight}
      ref={wrapperRef}
    >
      <IconWrapper $isHighlight={isHighlight}>
        {displayedCounter ? (
          <Counter $isHighlight={isHighlight} $pad={(counter ?? 0) > 99}>
            {displayedCounter}
          </Counter>
        ) : null}
        {SmartIcon}
        {!isHighlight && displayUnseenChangesTag ? <UnseenChangesTag /> : null}
      </IconWrapper>
      {SmartLabel}
    </TabBarItemWrapper>
  );
};

export default React.memo<NavigationTabBarItemProps>(NavigationTabBarItem);
